import { useState, useEffect, useRef, useCallback } from 'react'
import { useDispatch } from 'react-redux'
import moment from 'moment'
import toast from 'react-hot-toast'
import { fetchData, downloadFile } from '../helpers/fetchData'

const useAsyncReport = (config) => {
  const {
    addJobAction,
    updateJobAction,
    removeJobAction,
    createEndpoint,
    statusEndpoint,
    downloadEndpoint,
    fileNamePrefix,
    buildPayload,
    existingJobs = [] // Add this to receive existing jobs from Redux
  } = config

  const resolveEndpoint = (endpoint, filters) =>
    typeof endpoint === 'function' ? endpoint(filters) : endpoint

  const dispatch = useDispatch()
  const [loading, setLoading] = useState(false)
  const [downloadingJobs, setDownloadingJobs] = useState({})
  const pollingRefs = useRef({})

  const stopPolling = useCallback((jobId) => {
    if (pollingRefs.current[jobId]) {
      clearInterval(pollingRefs.current[jobId])
      delete pollingRefs.current[jobId]
    }
  }, [])

  const stopAllPolling = useCallback(() => {
    Object.keys(pollingRefs.current).forEach(jobId => {
      clearInterval(pollingRefs.current[jobId])
    })
    pollingRefs.current = {}
  }, [])

  const pollStatus = useCallback((jobId, filters) => {
    stopPolling(jobId)
    const resolvedStatusEndpoint = resolveEndpoint(statusEndpoint, filters)
    
    console.log(`[useAsyncReport] Starting polling for job ${jobId}`)
    
    pollingRefs.current[jobId] = setInterval(async () => {
      console.log(`[useAsyncReport] Polling status for job ${jobId}...`)
      try {
        const res = await fetchData('GET', `${resolvedStatusEndpoint}/${jobId}/status`)
        console.log(`[useAsyncReport] Status response for ${jobId}:`, res)
        if (!res) return

        const updated = {
          jobId,
          status: res.status,
          format: res.format,
          progress: res.progress_percentage || 0,
          processedCount: res.processed_count || 0,
          remainingCount: res.remaining_count || 0,
          totalRecords: res.total_records || 0,
          noData: !!res.no_data,
          eta: res.estimated_completion_time || null,
          downloadUrl: res.download_url || '',
          error: res.error_message || '',
          filters: filters,
          createdAt: new Date().toISOString()
        }

        dispatch(updateJobAction(updated))

        if (res.status === 'completed') {
          console.log(`[useAsyncReport] Job ${jobId} completed, stopping polling`)
          stopPolling(jobId)
          toast.success('Report is ready — click the download icon to save the file')
        } else if (res.status === 'failed') {
          console.log(`[useAsyncReport] Job ${jobId} failed, stopping polling`)
          stopPolling(jobId)
          toast.error(res.error_message || 'Report generation failed')
        }
      } catch (error) {
        console.error('[useAsyncReport] Polling error:', error)
        stopPolling(jobId)
      }
    }, 5000)
  }, [dispatch, updateJobAction, statusEndpoint, stopPolling])

  // Poll existing active jobs - check whenever existingJobs changes
  useEffect(() => {
    console.log('[useAsyncReport] Checking for active jobs to poll...', existingJobs)
    if (existingJobs && existingJobs.length > 0) {
      existingJobs.forEach(job => {
        if (job && ['queued', 'processing'].includes(job.status)) {
          // Only start polling if not already polling this job
          if (!pollingRefs.current[job.jobId]) {
            console.log(`[useAsyncReport] Found active job ${job.jobId}, starting polling`)
            pollStatus(job.jobId, job.filters)
          } else {
            console.log(`[useAsyncReport] Job ${job.jobId} already being polled`)
          }
        } else if (job && pollingRefs.current[job.jobId]) {
          // Stop polling if job is no longer active
          console.log(`[useAsyncReport] Job ${job.jobId} no longer active, stopping polling`)
          stopPolling(job.jobId)
        }
      })
    }
  }, [existingJobs, pollStatus, stopPolling])

  // Cleanup on unmount only
  useEffect(() => {
    return () => {
      console.log('[useAsyncReport] Component unmounting, stopping all polling')
      stopAllPolling()
    }
  }, [stopAllPolling])

  const startJob = async (filters, format = 'pdf') => {
    setLoading(true)

    try {
      const payload = buildPayload(filters, format)
      console.log('Creating job with payload:', payload)
      const res = await fetchData('POST', resolveEndpoint(createEndpoint, filters), payload)
      console.log('Job creation response:', res)

      if (res?.total_records === 0) {
        setLoading(false)
        toast('No data found for the selected filters', { icon: 'ℹ️' })
        return null
      }

      if (res?.job_id) {
        const jobData = {
          jobId: res.job_id,
          status: res.status || 'queued',
          format,
          progress: 0,
          totalRecords: res.total_records || 0,
          noData: false,
          processedCount: 0,
          remainingCount: 0,
          eta: null,
          downloadUrl: res.download_url || '',
          error: '',
          filters: filters,
          createdAt: new Date().toISOString()
        }
        
        dispatch(addJobAction(jobData))
        pollStatus(res.job_id, filters)
        setLoading(false)
        toast.success('Report job created successfully')
        return res.job_id
      } else {
        console.error('No job_id in response:', res)
        setLoading(false)
        toast.error('Invalid response from server')
        return null
      }
    } catch (error) {
      console.error('Error creating job:', error)
      setLoading(false)
      toast.error('Failed to create report job: ' + (error.message || 'Unknown error'))
      return null
    }
  }

  const downloadReport = async (job) => {
    if (!job?.jobId || downloadingJobs[job.jobId]) return
    
    setDownloadingJobs(prev => ({ ...prev, [job.jobId]: true }))
    
    try {
      const ext = job.format === 'pdf' ? 'pdf' : 'xlsx'
      const dateStr = job.filters?.start_date && job.filters?.end_date 
        ? `${moment(job.filters.start_date).format('YYYY-MM-DD')}_${moment(job.filters.end_date).format('YYYY-MM-DD')}`
        : moment().format('YYYY-MM-DD')
      const fn = `${fileNamePrefix}_${dateStr}.${ext}`
      
      await downloadFile('GET', `${resolveEndpoint(downloadEndpoint, job.filters)}/${job.jobId}/download`, {}, fn)
    } catch (error) {
      toast.error('Download failed')
    } finally {
      setDownloadingJobs(prev => {
        const updated = { ...prev }
        delete updated[job.jobId]
        return updated
      })
    }
  }

  const removeJob = (jobId) => {
    stopPolling(jobId)
    dispatch(removeJobAction(jobId))
    toast.success('Report removed')
  }

  const retryJob = async (job) => {
    if (job?.filters) {
      await startJob(job.filters, job.format)
    }
  }

  return {
    loading,
    downloadingJobs,
    startJob,
    downloadReport,
    removeJob,
    retryJob,
    pollStatus
  }
}

export default useAsyncReport
